import { MediaType } from "constants/enums";

import { CreateProductRequestBody } from "../apis/useCreateProduct";
import {
  ProductFormValues,
  ProductVariantFormValue
} from "../components/forms/ProductDetailsForm/ProductDetailsForm.types";
import {
  ProductBothTogetherMethod,
  ProductVideoVariant
} from "../constants/enums";
import { getMediaFilesIdsByProductVideoVariant } from "./getMediaFilesIdsByProductVideoVariant";

interface FormDataWithCompressedPreviewIdInVariants extends ProductFormValues {
  variants: Array<ProductVariantFormValue & { compressedPreviewId: string }>;
}

export const convertFormDataToCreateRequestBody = async (
  formData: FormDataWithCompressedPreviewIdInVariants,
  subSubCategoryId: string
): Promise<CreateProductRequestBody> => {
  const {
    description,
    isChapshopChoice,
    relatedAttributesValues,
    additionalAttributesValuesIds,
    variants,
    productVideoVariant,
    generalVideo
  } = formData;

  const isOneMainVideoVariant =
    productVideoVariant === ProductVideoVariant.ONE_MAIN_VIDEO;

  const isForEachVideoVariant =
    productVideoVariant === ProductVideoVariant.FOR_EACH_VIDEO;

  const variantsWithNumbers = variants.map((variant) => {
    const variantIdsBoughtTogether = (
      formData.productBothTogetherMethod ===
      ProductBothTogetherMethod.NO_PRODUCTS
        ? []
        : formData.productBothTogetherMethod ===
            ProductBothTogetherMethod.ONE_SET_FOR_ALL_VARIANTS
          ? variants[0].variantsBoughtTogether
          : variant.variantsBoughtTogether
    ).map((product) => product.id);

    const firstImageId = variant.images.find(
      (image) => image.mediaType === MediaType.IMAGE
    )?.id;

    return {
      name: variant.name,
      shortTitle: variant.shortTitle?.trim() || null,
      sku: variant.sku,
      shortId: variant.shortId,
      colorAttributeValueId: variant.colorAttributeValueId,
      barcodes: variant.barcodes.filter((barcode) => barcode.value),
      imagesIds: getMediaFilesIdsByProductVideoVariant(
        variant.images,
        productVideoVariant
      ),
      previewId: firstImageId,
      compressedPreviewId: variant.compressedPreviewId,
      videoPreviewId:
        isForEachVideoVariant &&
        variant.images.some((img) => img.mediaType === MediaType.VIDEO)
          ? variant.videoThumbnail?.id || null
          : null,
      badgeId: variant.badgeId || null,
      weight: Number(variant.weight),
      volume: Number(variant.volume),
      allowSellingBelowBuyingPrice: variant.allowSellingBelowBuyingPrice,
      buyingPrice: Number(variant.buyingPrice),
      merchantPrice: Number(variant.merchantPrice),
      standardPrice: Number(variant.standardPrice),
      intermediateResellerPrice: Number(variant.intermediateResellerPrice),
      confirmedResellerPrice: Number(variant.confirmedResellerPrice),
      influencerPrice: Number(variant.influencerPrice),
      recommendedSellingPrice: variant.recommendedSellingPrice
        ? Number(variant.recommendedSellingPrice)
        : null,
      discount:
        variant.activeDiscountType === "percentage"
          ? Number(variant.discount) || 0
          : 0,
      discountAmount:
        variant.activeDiscountType === "amount"
          ? Number(variant.discountAmount) || 0
          : 0,
      suggestionDiscount:
        variant.activeSuggestionDiscountType === "percentage"
          ? Number(variant.suggestionDiscount) || 0
          : 0,
      suggestionDiscountAmount:
        variant.activeSuggestionDiscountType === "amount"
          ? Number(variant.suggestionDiscountAmount) || 0
          : 0,
      commission: variant.commission ? Number(variant.commission) : undefined,
      commissionPercentage: variant.commissionPercentage
        ? Number(variant.commissionPercentage)
        : undefined,
      // Always 0: the flat influencer commission is gone from the form, and
      // sending it clears whatever an older product still carries.
      influencerCommission: 0,
      influencerCommissionPercentage: variant.influencerCommissionPercentage
        ? Number(variant.influencerCommissionPercentage)
        : undefined,
      variantIdsBoughtTogether: variantIdsBoughtTogether.length
        ? variantIdsBoughtTogether
        : undefined,
      bulkDiscounts: variant.bulkDiscounts.map((discountItem) => ({
        ...discountItem,
        discount: Number(discountItem.discount)
      }))
    };
  });

  return {
    description,
    isChapshopChoice,
    subSubCategoryId,
    additionalAttributesValuesIds: additionalAttributesValuesIds.map(
      (additionalAttribute) => String(additionalAttribute.valueId)
    ),
    relatedAttributesValues: relatedAttributesValues.filter(
      (attr) => attr.value
    ),
    generalVideoId: isOneMainVideoVariant ? generalVideo?.[0].id : null,
    generalVideoPreviewId: isOneMainVideoVariant
      ? formData.generalVideoThumbnail?.id || null
      : null,
    realVideoId: formData.realVideo?.[0]?.id ?? null,
    googleDriveLink: formData.googleDriveLink || null,
    variants: variantsWithNumbers
  };
};
